import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/Global.css";
import styles from "./Login.module.css";
import WelcomePanel from "../components/auth/WelcomePanel";
import LoginForm from "../components/auth/LoginForm";
import { useAuthContext } from "../contexts/AuthContext";
import LegacyUserModal from "../components/auth/LegacyUserModal";
import Footer from "../components/Footer";
import {
  consumeAuthReturn,
  peekAuthReturn,
  rememberAuthReturn,
  safeInternalPath,
  signupReturnFromMetadata,
  signupUtmFromMetadata,
} from "../utils/conversionJourney";
import { restoreSignupAttribution } from "../utils/affiliateStorage";
/**
 * Login page component that assembles all auth-related components
 * Uses glassmorphism styling for a modern, translucent appearance
 * Includes legacy user migration workflow
 *
 * @returns {JSX.Element} - Rendered login page
 */
const Login: React.FC = () => {
  const { signInWithPassword, loading, user } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [loginError, setLoginError] = useState<string | null>(null);

  // Legacy user modal state
  const [showLegacyModal, setShowLegacyModal] = useState(false);
  const [legacyUserData, setLegacyUserData] = useState<any>(null);
  const [currentEmail, setCurrentEmail] = useState("");
  const stateReturn = safeInternalPath((location.state as any)?.from);
  const returnTo = stateReturn || peekAuthReturn();

  useEffect(() => {
    if (stateReturn) rememberAuthReturn(stateReturn);
  }, [stateReturn]);

  useEffect(() => {
    const isAuthCallback = new URLSearchParams(location.search).get("auth_callback") === "1";
    if (!isAuthCallback || !user) return;
    // A confirmation link may open in another browser than the signup (e.g.
    // Safari after a TikTok in-app signup); fall back to the account's record.
    const metadata = (user as any).user_metadata;
    const createdAt = (user as any).created_at;
    const signupUtm = signupUtmFromMetadata(metadata, createdAt);
    restoreSignupAttribution(signupUtm, metadata?.signup_landing_path, signupUtm ? metadata?.signup_coupon : null);
    const destination = consumeAuthReturn()
      || returnTo
      || signupReturnFromMetadata(metadata, createdAt)
      || "/";
    navigate(destination, { replace: true });
  }, [location.search, navigate, returnTo, user]);

  /**
   * Check if user exists in legacy_users table
   * @param {string} email - User email to check
   */
  const checkLegacyUser = async (email: string) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";
      const response = await fetch(`${API_URL}/api/check-legacy-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error("Failed to check legacy user");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error checking legacy user:", error);
      return { is_legacy_user: false, migrated: false };
    }
  };

  /**
   * Handle login form submission with legacy user check
   * @param {string} email - User email
   * @param {string} password - User password
   */
  const handleLogin = async (email: string, password: string) => {
    setLoginError(null);
    setCurrentEmail(email);

    /* ------------------------------------------------------------------
     * 1️⃣  QUICK legacy lookup – no password needed
     * ------------------------------------------------------------------ */
    const legacyCheck = await checkLegacyUser(email);

    if (legacyCheck.is_legacy_user && !legacyCheck.migrated) {
      // Show migration modal immediately instead of submitting wrong password
      setLegacyUserData(legacyCheck.user_data);
      setShowLegacyModal(true);
      return;
    }

    /* ------------------------------------------------------------------
     * 2️⃣  Not an unmigrated legacy user → proceed with regular login
     * ------------------------------------------------------------------ */
    const { error } = await signInWithPassword(email, password);

    if (error) {
      // Check for email not confirmed error
      if (
        error.message.includes("Email not confirmed") ||
        error.message.includes("email_not_confirmed") ||
        error.message.includes("signup_requires_confirmation")
      ) {
        setLoginError(
          "Please check your email and click the confirmation link to activate your account."
        );
        (window as any).showNotification?.(
          "Please check your email and click the confirmation link to activate your account.",
          "warning"
        );
        return;
      }

      // If login failed, check if this is a legacy user
      if (
        error.message.includes("Invalid login credentials") ||
        error.message.includes("invalid_credentials") ||
        error.message.includes("wrong_password")
      ) {
        const legacyCheck = await checkLegacyUser(email);

        if (legacyCheck.is_legacy_user && !legacyCheck.migrated) {
          // Legacy user who hasn't migrated yet - show migration modal
          setLegacyUserData(legacyCheck.user_data);
          setShowLegacyModal(true);
          return;
        } else if (legacyCheck.is_legacy_user && legacyCheck.migrated) {
          // Legacy user who has already migrated but entered wrong password
          setLoginError(
            "Incorrect password. Please check your password and try again."
          );
          (window as any).showNotification?.(
            "Incorrect password. Please check your password and try again.",
            "error"
          );
          return;
        } else if (!legacyCheck.is_legacy_user) {
          // User not in legacy_users table - check if they exist in auth
          if (legacyCheck.auth_user_exists) {
            // Regular user (exists in auth) with wrong password
            setLoginError(
              "Incorrect password. Please check your password and try again."
            );
            (window as any).showNotification?.(
              "Incorrect password. Please check your password and try again.",
              "error"
            );
          } else {
            // User doesn't exist anywhere - suggest signup
            setLoginError(
              "Email not found. Please sign up for a new account or check your email address."
            );
            (window as any).showNotification?.(
              "Email not found. Please sign up for a new account or check your email address.",
              "error"
            );
          }
          return;
        }
      }

      // Handle other types of login errors (network, server, etc.)
      if (
        error.message.includes("fetch") ||
        error.message.includes("network")
      ) {
        setLoginError(
          "Network error. Please check your connection and try again."
        );
        (window as any).showNotification?.(
          "Network error. Please check your connection and try again.",
          "error"
        );
      } else {
        setLoginError(
          error.message || "Login failed. Please try again or contact support."
        );
        (window as any).showNotification?.(
          error.message || "Login failed. Please try again or contact support.",
          "error"
        );
      }
    } else {
      // Post-signup actions are now handled centrally in useAuth.ts
      // Just show a generic success message
      (window as any).showNotification?.(
        "🎉 Welcome back! You've successfully signed in.",
        "success"
      );

      // Redirect to the page the user was trying to visit, or home
      const pendingReturn = consumeAuthReturn();
      const redirectTo = stateReturn || pendingReturn || "/";
      navigate(redirectTo, { state: location.state });
    }
  };

  /**
   * Handle legacy user migration completion
   */
  const handleMigrationComplete = () => {
    // Refresh the page or redirect to trigger auth state update
    setTimeout(() => {
      const pendingReturn = consumeAuthReturn();
      navigate(stateReturn || pendingReturn || "/");
    }, 1000);
  };

  return (
    <>
      <div className={styles.authLayoutOuter}>
        <div className={styles.authLayoutContent}>
          {/* Left panel with welcome message */}
          <div className={styles.leftPanel}>
            <WelcomePanel
              title="Welcome back."
              subtitle="Continue your C-Shaped study session with a calmer clinical workspace."
            />
          </div>

          {/* Right panel with login form */}
          <div className={styles.rightPanel}>
            <LoginForm
              onSubmit={handleLogin}
              isLoading={loading}
              error={loginError}
              returnTo={returnTo}
            />
          </div>
        </div>
      </div>

      {/* Legacy User Migration Modal */}
      <LegacyUserModal
        isOpen={showLegacyModal}
        onClose={() => setShowLegacyModal(false)}
        email={currentEmail}
        userData={legacyUserData}
        onMigrationComplete={handleMigrationComplete}
      />
      <Footer />
    </>
  );
};

export default Login;
